import { useState, useEffect, useRef } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { useAuthStore } from '../../contexts/auth/useAuthStore';
import { GET_ORGANIZATIONS_SWITCHER } from '../../lib/graphql/queries/organizations';

const PAGE_SIZE = 25;

interface SwitcherOrg {
  id: string;
  org_name: string;
}

interface GetOrganizationsData {
  organizations: {
    organizations: { organizationId: string; orgName: string }[];
    total: number;
    hasMore: boolean;
  };
}

export const useOrganizationSwitcher = () => {
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<SwitcherOrg[]>([]);
  const prevSearch = useRef('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to first page and clear accumulated on new search.
  // Clearing prevents stale page-N results from briefly being appended onto
  // the old list before the page-0 response lands.
  useEffect(() => {
    if (debouncedSearch !== prevSearch.current) {
      prevSearch.current = debouncedSearch;
      setPage(0);
      setAccumulated([]);
    }
  }, [debouncedSearch]);

  const { data, loading } = useApolloQuery<GetOrganizationsData>(GET_ORGANIZATIONS_SWITCHER, {
    variables: {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      search: debouncedSearch || undefined,
    },
    skip: !user?.email,
    fetchPolicy: 'cache-and-network',
  });

  // Append new page results to accumulated list. Only act once data has
  // arrived for the current variables — checking `data` (not array length)
  // lets empty search results correctly replace the previous list.
  useEffect(() => {
    if (!data?.organizations) return;
    const incoming = data.organizations.organizations.map((o) => ({
      id: o.organizationId,
      org_name: o.orgName,
    }));
    if (page === 0) {
      setAccumulated(incoming);
    } else {
      setAccumulated((prev) => {
        const existingIds = new Set(prev.map((o) => o.id));
        return [...prev, ...incoming.filter((o) => !existingIds.has(o.id))];
      });
    }
  }, [data, page]);

  const total = data?.organizations?.total ?? 0;
  const hasMore = data?.organizations?.hasMore ?? false;

  return {
    data: accumulated,
    isLoading: loading,
    total,
    hasNext: hasMore,
    hasPrev: page > 0,
    page,
    setPage,
    search,
    setSearch,
  };
};
